import React, { useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const ProductCard = ({ product, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: index * 0.05 }}
  >
    <Box
      onClick={onClick}
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#080808',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: { xs: '140px', md: '180px' },
          backgroundColor: '#1A1A1A',
          backgroundImage: `url(${product.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <Box sx={{ p: 2 }}>
        <Typography
          sx={{
            fontFamily: 'Oswald',
            fontSize: { xs: '14px', md: '16px' },
            fontWeight: 600,
            color: '#FFFFFF',
            letterSpacing: '0.05em',
            mb: 0.5,
          }}
        >
          {product.name}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontSize: '11px',
            fontWeight: 300,
            color: '#A8C5E8',
            letterSpacing: '0.05em',
          }}
        >
          {product.category}
        </Typography>
      </Box>
    </Box>
  </motion.div>
);

const ProductListingPage = ({
  title,
  subtitle,
  products,
  categories,
  titleColor = '#1A1A1A',
  backButtonPosition = 'left',
  contentTopPadding = { xs: 14, md: 16 },
  backButtonMarginBottom = 4,
  headerInline = false,
  onProductClick,
}) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const filteredProducts = selectedCategory === 'All'
    ? products
    : products.filter((product) => product.category === selectedCategory);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url(/img/23.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          style={{ width: '100%' }}
        >
          <Box sx={{ pt: contentTopPadding, px: { xs: 3, md: 6, lg: 10 }, pb: 10 }}>
            {headerInline ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  justifyContent: 'space-between',
                  gap: 2,
                  flexDirection: { xs: 'column', md: 'row' },
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Oswald',
                      fontSize: { xs: '40px', sm: '52px', md: '64px' },
                      fontWeight: 700,
                      color: titleColor,
                      letterSpacing: '0.05em',
                      lineHeight: 0.9,
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Oswald',
                      fontSize: { xs: '40px', sm: '52px', md: '64px' },
                      fontWeight: 700,
                      color: '#2858A8',
                      letterSpacing: '0.05em',
                      lineHeight: 0.9,
                    }}
                  >
                    {subtitle}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: 'pointer',
                    backgroundColor: 'rgba(216, 216, 188, 0.8)',
                    backdropFilter: 'blur(8px)',
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    maxWidth: 'fit-content',
                  }}
                  onClick={() => navigate('/products/categories')}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <Typography sx={{ fontFamily: 'Roboto', fontSize: '16px', fontWeight: 500, color: '#1A1A1A' }}>
                    Back to Products
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: backButtonPosition === 'right' ? 'flex-end' : 'flex-start',
                    gap: 2,
                    mb: backButtonMarginBottom,
                    cursor: 'pointer',
                    width: '100%',
                    backgroundColor: 'rgba(216, 216, 188, 0.8)',
                    backdropFilter: 'blur(8px)',
                    px: 2,
                    py: 1,
                    borderRadius: '8px',
                    maxWidth: 'fit-content',
                    ml: backButtonPosition === 'right' ? 'auto' : 0,
                  }}
                  onClick={() => navigate('/products/categories')}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <Typography sx={{ fontFamily: 'Roboto', fontSize: '16px', fontWeight: 500, color: '#1A1A1A' }}>
                    Back to Products
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontFamily: 'Oswald',
                    fontSize: { xs: '48px', sm: '64px', md: '72px' },
                    fontWeight: 700,
                    color: titleColor,
                    letterSpacing: '0.05em',
                    lineHeight: 0.9,
                    mb: 1,
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Oswald',
                    fontSize: { xs: '48px', sm: '64px', md: '72px' },
                    fontWeight: 700,
                    color: '#2858A8',
                    letterSpacing: '0.05em',
                    lineHeight: 0.9,
                    mb: 6,
                  }}
                >
                  {subtitle}
                </Typography>
              </>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 6 }}>
              {categories.map((category) => (
                <Box
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    backgroundColor: selectedCategory === category ? '#2858A8' : '#080808',
                    color: '#FFFFFF',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#2858A8',
                    },
                  }}
                >
                  {category}
                </Box>
              ))}
            </Box>

            <Grid container spacing={3}>
              {filteredProducts.map((product, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={product.id}>
                  <ProductCard
                    product={product}
                    index={index}
                    onClick={onProductClick ? () => onProductClick(product) : undefined}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default ProductListingPage;